import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { PageHeader } from '../components/Layout';
import { Badge, cn } from '../components/ui';
import { api } from '../lib/api';
import type { PushRoute } from '../lib/types';
import { AutomationsTab } from './notifications/Automations';
import { BlacklistTab } from './notifications/Blacklist';
import { ComposeTab } from './notifications/Compose';
import { HistoryTab } from './notifications/History';
import { TemplatesTab } from './notifications/Templates';
import { VersionBlocksTab } from './notifications/VersionBlocks';

type Tab =
  | 'compose'
  | 'automations'
  | 'templates'
  | 'history'
  | 'blacklist'
  | 'builds';

const TABS: { id: Tab; label: string }[] = [
  { id: 'compose', label: 'Compose' },
  { id: 'automations', label: 'Automations' },
  { id: 'templates', label: 'Templates' },
  { id: 'history', label: 'History' },
  { id: 'blacklist', label: 'Blacklist' },
  { id: 'builds', label: 'Blocked builds' },
];

export function NotificationsPage() {
  const [tab, setTab] = useState<Tab>('compose');

  return (
    <>
      <PageHeader
        title="Notifications"
        description="Broadcast, schedule and translate push messages"
      />

      <PushRoutingNotice />

      <div
        role="tablist"
        aria-label="Notification sections"
        className="mb-4 flex flex-wrap gap-1 rounded-lg border border-line bg-surface p-1"
      >
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={tab === item.id}
            onClick={() => setTab(item.id)}
            className={cn(
              'min-w-[92px] flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors',
              tab === item.id
                ? 'bg-brand-soft text-brand'
                : 'text-ink-2 hover:bg-raised hover:text-ink',
            )}
          >
            {item.label}
          </button>
        ))}
      </div>

      {tab === 'compose' && <ComposeTab onSent={() => setTab('history')} />}
      {tab === 'automations' && <AutomationsTab />}
      {tab === 'templates' && <TemplatesTab />}
      {tab === 'history' && <HistoryTab />}
      {tab === 'blacklist' && <BlacklistTab />}
      {tab === 'builds' && <VersionBlocksTab />}
    </>
  );
}

/**
 * Warns when an (app, platform) pair routes to a transport whose credentials
 * are missing — otherwise those devices are silently skipped at send time and
 * the campaign looks like it simply reached fewer people.
 */
function PushRoutingNotice() {
  const routing = useQuery({
    queryKey: ['push-routing'],
    queryFn: () => api.get<PushRoute[]>('/api/push/routing'),
    staleTime: 5 * 60_000,
  });

  const broken = routing.data?.filter((route) => !route.configured) ?? [];
  if (broken.length === 0) return null;

  return (
    <div className="mb-4 rounded-lg border border-warn/30 bg-warn-soft px-3.5 py-3">
      <p className="flex items-center gap-2 text-sm font-medium text-warn">
        <AlertTriangle size={16} />
        Some apps cannot receive pushes yet
      </p>
      <p className="mt-1 text-xs text-warn">
        These pairs route to a provider with no credentials configured. Devices
        matching them are skipped rather than delivered to.
      </p>
      <ul className="mt-2 flex flex-wrap gap-1.5">
        {broken.map((route) => (
          <li key={`${route.appKey}:${route.platform}`}>
            <Badge tone="warn">
              {route.appKey} · {route.platform} → {route.provider}
            </Badge>
          </li>
        ))}
      </ul>
    </div>
  );
}
